/**
 * TeamPickerColumn — one side's roster + ready-up card. Renders the
 * CreateTab for the local player and a read-only preview for the
 * opponent.
 *
 * When `draftComplete` is false, the column collapses to a "Available
 * after draft" lock state — used by the deck-builder lobby to gate
 * roster creation behind the draft step. Defaults to true so vanilla
 * H2H callers (which don't care about a draft) keep working with no
 * prop changes.
 */

import React, { useState, useRef } from "react";
import { CheckCircle, Lock } from "lucide-react";

import CreateTab from "@/components/lobby/CreateTab";

export default function TeamPickerColumn({
  label, accent, isMe, isReady, gameId, teamType, game,
  onReadyToggle, connected,
  draftComplete = true,
}) {
  const [customSaved, setCustomSaved] = useState(false);
  const [customName, setCustomName] = useState("");
  // True while "Edit roster" has the builder open. Without it a team already
  // saved on the server kept `hasTeam` true, so Edit roster un-readied you but
  // never showed the builder.
  const [editing, setEditing] = useState(false);
  const createTabRef = useRef({ canSave: false, save: null });
  // Mirror of the builder's readiness, so this column re-renders when it changes.
  const [create, setCreate] = useState({ canSave: false, hasStarted: false });

  const currentTeam = game?.[`${teamType}_team`];
  const hasCustomTeam = (currentTeam?.players?.length || 0) > 0;
  const customTeamName = currentTeam?.name;

  // Lineup gate — if a player draft ran, require 9 batting slots to be filled
  // before allowing ready-up. Skipped when the roster doesn't have 9
  // non-pitchers (e.g. a minimal draft config) so the gate can never be
  // impossible to satisfy.
  const playerDraftComplete = game?.player_draft_phase === "complete";
  const nonPitcherCount = (currentTeam?.players || []).filter(p => p?.position !== "P").length;
  const battingOrderLength = currentTeam?.batting_order?.length ?? 0;
  const lineupComplete = !playerDraftComplete || nonPitcherCount < 9 || battingOrderLength >= 9;

  const accentClasses = accent === "away"
    ? { border: "border-blue-500", bg: "bg-blue-500/10", text: "text-blue-400", era: "text-blue-400" }
    : { border: "border-amber-500", bg: "bg-amber-500/10", text: "text-amber-400", era: "text-amber-400" };

  const handleCustomSaved = (name) => {
    setCustomSaved(true);
    setCustomName(name);
    setEditing(false);
    if (isReady) onReadyToggle(false);
  };

  const hasTeam = !editing && (customSaved || hasCustomTeam);

  // Pre-draft lock state — the deck-builder lobby uses this to hide the
  // roster picker until the draft completes.
  if (!draftComplete) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{label}</p>
            <p className={`text-xs font-bold mt-0.5 ${isMe ? accentClasses.text : "text-slate-600"}`}>
              {isMe ? "Your roster" : "Opponent"}
            </p>
          </div>
        </div>
        <div className="rounded-xl border border-slate-700/30 bg-slate-900/20 px-4 py-8 flex flex-col items-center justify-center gap-2 text-center">
          <Lock size={20} className="text-slate-700" />
          <p className="text-[11px] text-slate-700">Available after draft</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 min-h-0">
      {/* Column header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{label}</p>
          <p className={`text-xs font-bold mt-0.5 ${isMe ? accentClasses.text : "text-slate-600"}`}>
            {isMe ? "Your roster" : "Opponent"}
          </p>
        </div>
        {isReady && (
          <span className="flex items-center gap-1 text-[10px] font-black text-emerald-400 uppercase tracking-widest">
            <CheckCircle size={12} /> Ready
          </span>
        )}
      </div>

      {/* Team builder */}
      {isMe ? (
        hasTeam ? (
          <div className="space-y-2">
            <div className={`rounded-xl border p-3.5 ${accentClasses.border} ${accentClasses.bg}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-black text-sm text-white">{customName || customTeamName}</p>
                  <p className={`text-[9px] font-bold uppercase tracking-widest mt-0.5 ${accentClasses.era}`}>Custom Roster</p>
                </div>
                <CheckCircle size={14} className={`${accentClasses.text} flex-shrink-0 mt-0.5`} />
              </div>
            </div>
            <button onClick={() => { setCustomSaved(false); setEditing(true); if (isReady) onReadyToggle(false); }}
              className="w-full text-[10px] text-slate-500 hover:text-slate-300 py-1 transition-colors">
              Edit roster ↻
            </button>
          </div>
        ) : (
          <CreateTab gameId={gameId} teamType={teamType} onTeamSaved={handleCustomSaved}
            stateRef={createTabRef} onStateChange={setCreate} />
        )
      ) : (
        hasCustomTeam ? (
          <div className={`rounded-xl border p-3.5 ${accentClasses.border} ${accentClasses.bg}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-black text-sm text-white">{customTeamName}</p>
                <p className={`text-[9px] font-bold uppercase tracking-widest mt-0.5 ${accentClasses.era}`}>Custom · Selected</p>
              </div>
              <CheckCircle size={14} className={`${accentClasses.text} flex-shrink-0 mt-0.5`} />
            </div>
          </div>
        ) : (
          <p className="text-[10px] text-slate-700 italic text-center py-6">Opponent is building their roster…</p>
        )
      )}

      {/* Ready Up */}
      {isMe ? (() => {
        const hasSavedCustom = customSaved || hasCustomTeam;

        let btnLabel, disabled = false, action = null;

        if (!connected) {
          btnLabel = "Reconnecting…";
          disabled = true;
        } else if (isReady) {
          btnLabel = "✓ Ready · Tap to edit";
          action = () => onReadyToggle(false);
        } else if (create.canSave) {
          btnLabel = "Save Roster & Ready Up →";
          // Read the save function at click time. Captured at render time it
          // closed over an older team name, so a fresh edit saved the old one.
          action = async () => {
            const save = createTabRef.current?.save;
            const ok = save ? await save() : false;
            if (ok) onReadyToggle("custom");
          };
        } else if (!lineupComplete) {
          btnLabel = `Set full batting order to ready up (${battingOrderLength}/9)`;
          disabled = true;
        } else if (hasSavedCustom && !create.hasStarted) {
          btnLabel = "Ready Up →";
          action = () => { setEditing(false); onReadyToggle("custom"); };
        } else {
          btnLabel = "Complete roster to ready up";
          disabled = true;
        }

        return (
          <button
            onClick={() => action && action()}
            disabled={disabled}
            className={`w-full py-3 rounded-xl font-black text-sm tracking-wide transition-all ${
              disabled
                ? "bg-slate-800 text-slate-500 cursor-not-allowed"
                : isReady
                  ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/20"
                  : "bg-amber-500 hover:bg-amber-400 text-black"
            }`}>
            {btnLabel}
          </button>
        );
      })() : (
        <div className="text-center py-2">
          {isReady ? (
            <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest">Opponent ready</p>
          ) : (
            <p className="text-[10px] text-slate-600 italic">Building roster…</p>
          )}
        </div>
      )}
    </div>
  );
}
